import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@features/authentication/store/authStore';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import type { QuotationFilterTab, QuotationStats } from '../types/quotation.types';

const TAB_PARAM = 'status';

interface TabDef {
  value: QuotationFilterTab;
  label: string;
  permission?: string;
  countKey?: keyof QuotationStats;
}

// Auto-drafts sits next to All so the email-automation queue is the first thing reviewers
// see when they arrive. It is gated on quotations.review_inbox; users without the permission
// don't see the tab at all (and the URL fallback drops 'auto-drafts' back to 'all' below).
const ALL_TABS: TabDef[] = [
  { value: 'all', label: 'All' },
  {
    value: 'auto-drafts',
    label: 'Auto-drafts',
    permission: PERMISSIONS.QUOTATIONS_REVIEW_INBOX,
    countKey: 'autoDraft',
  },
  { value: 'drafts', label: 'Drafts', countKey: 'draft' },
  { value: 'submitted', label: 'Submitted', countKey: 'submitted' },
  {
    value: 'archived',
    label: 'Archived',
    permission: PERMISSIONS.QUOTATIONS_ARCHIVE_QUOTATION,
    countKey: 'archived',
  },
];

function useVisibleTabs(): TabDef[] {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  return useMemo(
    () => ALL_TABS.filter((t) => !t.permission || hasPermission(t.permission)),
    [hasPermission],
  );
}

export function useQuotationFilterTab(): [QuotationFilterTab, (next: QuotationFilterTab) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const visibleTabs = useVisibleTabs();
  const raw = searchParams.get(TAB_PARAM) as QuotationFilterTab | null;
  // Fall back to 'all' if URL holds an unknown tab OR a tab the current user can't see
  // (e.g. a user without review_inbox arrives via a shared link with status=auto-drafts).
  const active: QuotationFilterTab = visibleTabs.some((t) => t.value === raw)
    ? (raw as QuotationFilterTab)
    : 'all';

  const setActive = useCallback(
    (next: QuotationFilterTab) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next === 'all') {
            p.delete(TAB_PARAM);
          } else {
            p.set(TAB_PARAM, next);
          }
          p.delete('page');
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return [active, setActive];
}

interface QuotationStatusFilterTabsProps {
  value: QuotationFilterTab;
  onChange: (next: QuotationFilterTab) => void;
  stats?: QuotationStats | null;
}

export function QuotationStatusFilterTabs({
  value,
  onChange,
  stats,
}: QuotationStatusFilterTabsProps) {
  const visibleTabs = useVisibleTabs();
  return (
    <Tabs value={value} onValueChange={(v) => onChange(v as QuotationFilterTab)}>
      <TabsList>
        {visibleTabs.map((tab) => {
          const count = tab.countKey && stats ? stats[tab.countKey] : undefined;
          return (
            <TabsTrigger key={tab.value} value={tab.value} className="gap-1.5">
              {tab.label}
              {count !== undefined && count > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {count}
                </Badge>
              )}
            </TabsTrigger>
          );
        })}
      </TabsList>
    </Tabs>
  );
}
